/**
 * joinOnParse — recognises the simplest, most common ON predicate shape:
 * `‹colRef› = ‹colRef›` (each side a plain column reference — `id`, `a.id`,
 * `"order items"."alt film"`, `[Order Lines].[qty]`, `s.t.c`). When
 * `JoinEditableOnPill` sees this shape it opens the structured two-column
 * editor; anything richer (AND/OR, functions, casts, literals, other
 * comparisons) returns null and falls back to the free-text editor.
 *
 * Pure + conservative on purpose: it only green-lights predicates it can
 * round-trip losslessly through two column dropdowns. Each side resolves to
 * one of the statement's own tables (`listInScopeTables`); a re-pointed side
 * is written with that table's qualifier as the query spells it plus the
 * column's catalog spelling, delimited when it has no bare form.
 */

import type { SqlEngine } from '../../../../sql-grammar/model';
import { catalogIdentSql } from '../../../../sql-resolver/suggest/sqlIdentifierText';
import { splitColumnRef as splitRefParts, splitQuotedColumnRef } from '../common/selectedColumnKeys';
import { findInScopeTable, type InScopeTable } from './inScopeTables';

export interface SimpleJoinOn {
  readonly left: string;
  readonly right: string;
}

export function parseSimpleJoinOn(text: string): SimpleJoinOn | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;
  // Exactly one `=` outside delimiters, with a plain column reference on each
  // side: other comparison operators, boolean combinators, calls, casts and
  // literals all fail the side check.
  const eq = soleTopLevelEquals(trimmed);
  if (eq < 0) return null;
  const left = trimmed.slice(0, eq).trim();
  const right = trimmed.slice(eq + 1).trim();
  if (!splitRefParts(left) || !splitRefParts(right)) return null;
  return { left, right };
}

/**
 * `{ prefix, column }` of a column reference, delimiters stripped: the prefix
 * is the part naming the table / alias (the second-to-last of a multi-part
 * reference), `''` for a bare column.
 */
export function splitColumnRef(ref: string): { prefix: string; column: string } {
  const parts = splitRefParts(ref);
  if (!parts) return { prefix: '', column: ref.trim() };
  return { prefix: parts.length > 1 ? parts[parts.length - 2]! : '', column: parts[parts.length - 1]! };
}

export interface OnSide {
  readonly table: InScopeTable;
  /** The column's catalog spelling (the column dropdown's value). */
  readonly column: string;
}

/**
 * The in-scope table + column an ON side names, or null when its qualifier is
 * not one of the tables this ON can see or the column is not one of that
 * table's. The qualifier is matched the way the engine binds names
 * (`findInScopeTable`: quote-aware on PostgreSQL / Oracle, where `"a"` and
 * `"A"` are two tables; as spelled, then case-insensitively when unique,
 * elsewhere); the column exactly first, then case-insensitively.
 */
export function resolveOnSide(sideText: string, tables: readonly InScopeTable[], engine: SqlEngine): OnSide | null {
  const parts = splitQuotedColumnRef(sideText);
  if (!parts || parts.length < 2) return null;
  const table = findInScopeTable(parts[parts.length - 2]!, tables, engine);
  if (!table) return null;
  const column = parts[parts.length - 1]!.value;
  const names = table.columns.map((c) => c.qualified.name);
  const match = names.find((n) => n === column) ?? names.find((n) => n.toLowerCase() === column.toLowerCase());
  return match === undefined ? null : { table, column: match };
}

/** A side re-pointed through the dropdowns: `qualifier.column`, both dialect-exact. */
export function formatOnSide(table: InScopeTable, column: string, engine: SqlEngine): string {
  return `${table.qualifierSql}.${catalogIdentSql(column, engine)}`;
}

const CLOSERS: Readonly<Record<string, string>> = { '"': '"', '[': ']', '`': '`', "'": "'" };

/** Index of the only `=` outside delimited identifiers / string literals; -1 when there are none or several. */
function soleTopLevelEquals(text: string): number {
  let at = -1;
  for (let i = 0; i < text.length; i++) {
    const close = CLOSERS[text[i]!];
    if (close) {
      const end = text.indexOf(close, i + 1);
      if (end < 0) return -1;
      i = end;
      continue;
    }
    if (text[i] !== '=') continue;
    if (at >= 0) return -1;
    at = i;
  }
  return at;
}
